import * as React from 'react';
import styled from 'styled-components';
import locale from 'date-fns/locale/de';
import { isFuture, format } from 'date-fns';
import { get } from 'lodash';
import shortid from 'shortid';
import posed, { PoseGroup } from 'react-pose';

import {
	Title,
	GiftcardIcon,
	Button,
	CheckIcon,
	ErrorIcon,
	Headline,
	Typography,
} from '../material-ui';
import { WithData, client } from '../apollo-utils';
import styleObject from '../style-object';
import {
	VoucherCatalogQuery,
	BuyAndRedeemVoucherMutation,
	RedeemVoucherMutation,
} from '../queries';
import { QRCodeCard, VoucherCard, SelectButton, Alert, Infobox } from '../components';

const isVoucherAvailable = (voucher, user) =>
	voucher &&
	// is valid
	voucher.valid !== false &&
	// is not redeemed yey
	!voucher.redeemedDatetime &&
	// is still redeemable
	(!voucher.redeemableUntil || isFuture(voucher.redeemableUntil)) &&
	// criteria for vouchers that haven't been bought yet
	(voucher.qrCode ||
		// still has instances vailable
		((!voucher.remainingCapacity || voucher.remainingCapacity > 0) &&
			// user has enough coins to buy it
			(!(user && user.bonusProgramMembership) ||
				voucher.price <= user.bonusProgramMembership.coinBalance) &&
			// the available quantity per person is no more than the owned qunatity
			(!voucher.maxQuantityPerPerson ||
				!(user && user.bonusProgramMembership && user.bonusProgramMembership.ownedVouchers) ||
				user.bonusProgramMembership.ownedVouchers
					.filter(({ voucherClass }) => voucherClass.id === voucher.id)
					.reduce((acc) => acc + 1, 0) < voucher.maxQuantityPerPerson)));

const Wrapper = styled.div`
	flex: 1;
	padding: 3rem 2rem;
`;

const transition = { ease: 'easeInOut', duration: 300 };
const PoseWrapper = posed.div({
	enter: { opacity: 1, scale: 1, originX: 0, originY: 0, transition },
	exit: { opacity: 0, scale: 0.2, transition },
	flip: { transition },
});

const VoucherSelectWrapper = styled.div`
	display: flex;
	margin: 1rem 0;
	height: 4rem;
	align-items: center;
`;

const SectionSeparator = styled.div`
	margin: 2rem 0;
	border-bottom: 3px solid lightgray;
`;

const iconStyle = styleObject`
	margin-right: 0.5rem;
`;

const labelStyle = styleObject`
	display: flex;
`;

const BonusCardWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: start;
	margin: 1rem 0;
`;

const VoucherWallWrapper = styled.div`
	display: flex;
	flex-flow: row wrap;
	margin: 0 -0.5rem;
`;

const SubmitWrapper = styled.div`
	display: flex;
	flex-direction: row;
	margin: 2rem 0;
`;

const VoucherHeading = styled(Headline)`
	width: 100%;
	height: 2rem;
	margin: 2rem 0 1rem 0.5rem !important;
	color: ${(p) => p.theme.palette.action.active} !important;
`;

const VoucherCardPlaceholder = styled.div`
	height: 7rem;
	width: 100%;
	padding: 2rem;
	display: flex;
	align-items: center;
`;

const iconBulletStyle = styleObject`
	margin-right: 0.5rem;
	font-size: 1.2rem;
	vertical-align: text-top;
`;

const ErrorItem = styled.p`
	font-weight: bold;
`;

const formatDate = (date) => format(date, 'D. MMM YYYY', { locale });

const sendMutation = async (voucher, user) => {
	try {
		const result = await client.mutate({
			mutation: voucher.qrCode ? RedeemVoucherMutation : BuyAndRedeemVoucherMutation,
			variables: voucher.qrCode
				? { voucherQR: voucher.qrCode }
				: { voucherClassId: voucher.id, userId: user.id },
			errorPolicy: 'all',
		});
		return result;
	} catch (e) {
		if (e.networkError) {
			return { errors: [{ message: 'NETWORK_ERROR' }] };
		} else throw e;
	}
};

const getInitialState = () => ({
	alert: null,
	user: null,
	voucher: null,
	voucherSelection: false,
	key: shortid.generate(),
});

export default class RedeemScreen extends React.Component {
	state = getInitialState();

	static label = (
		<span style={labelStyle}>
			<GiftcardIcon style={iconStyle} />
			Einlösen
		</span>
	);

	qrRef = React.createRef();

	componentDidMount = () => {
		if (this.props.active && this.qrRef.current) this.qrRef.current.focus();
	};

	componentDidUpdate = (prevProps) => {
		if (this.props.active && !prevProps.active) {
			setTimeout(() => {
				this.qrRef.current && this.qrRef.current.focus();
			}, 100);
		}
	};

	handleQRCodeValueSet = ({ key, value }) => {
		if (key !== 'voucher' || isVoucherAvailable(value, this.state.user)) {
			this.setState((state) => ({
				[key]: value,
				voucherSelection:
					key === 'user'
						? Boolean(value)
						: key === 'voucher' && value
						? false
						: state.voucherSelection,
			}));
		} else {
			this.setState({
				alert: {
					title: 'Ungültig!',
					description: 'Gescannt Prämie ist ungültig, bitte andere auswählen.',
					onConfirm: this.handleCloseAlert,
					onAbort: null,
				},
			});
		}
	};

	handleVoucherSelect = (voucher) => {
		this.setState({ voucher, voucherSelection: false });
		window.scrollTo(0, 0);
	};

	handleToggleVoucherSelection = () =>
		this.setState((state) => ({ voucherSelection: !state.voucherSelection }));

	handleReset = () => {
		this.setState(getInitialState(), () => this.qrRef.current.focus());
	};

	handleSubmit = () => {
		const { voucher, user } = this.state;
		this.setState({
			alert: {
				title: 'Prämie Einlösen?',
				description: [
					`${voucher.title} (${voucher.price} Punkte)`,
					' ',
					voucher.description,
					' ',
					voucher.maxQuantityPerPerson && `Pro Person: ${voucher.maxQuantityPerPerson}`,
					voucher.remainingCapacity && `Verfügbar: ${voucher.remainingCapacity}`,
					voucher.bookableUntil && `Buchbas bis: ${formatDate(voucher.bookableUntil)}`,
					voucher.redeemableUntil && `Einlösbar bis: ${formatDate(voucher.redeemableUntil)}`,
					voucher.redeemedDatetime && `Eingelöst am: ${formatDate(voucher.redeemedDatetime)}`,
					voucher.createdDatetime && `Gekauft am: ${formatDate(voucher.createdDatetime)}`,
					voucher.valid === false && `Ungültiger Gutschein`,
				]
					.filter((t) => t)
					.join('\n'),
				onConfirm: async () => {
					this.setState({ alert: { loading: true, onAbort: null } });
					const { data, errors } = await sendMutation(voucher, user);
					if (data && data.result && data.result.success) {
						this.setState({
							alert: {
								title: 'Prämie eingelöst',
								description:
									'Die Prämie wurde bei Cinuru eingelöst. Bitte ggf. zusätzlich im Kassensystem verbuchen.',
								onConfirm: this.handleReset,
								onAbort: null,
							},
						});
					} else {
						this.setState({
							alert: {
								title: 'Unerwarteter Fehler',
								description: JSON.stringify(errors, null, 4),
							},
						});
					}
				},
			},
		});
	};

	handleCloseAlert = () => this.setState({ alert: null });

	render = () => {
		const { voucher, user, voucherSelection, alert, key } = this.state;
		const { cinemaId, cameraScannerEnabled } = this.props;
		const coinBalance = get(user, 'bonusProgramMembership.coinBalance', 0);
		const completed = voucher;
		return (
			<WithData
				query={VoucherCatalogQuery}
				// eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
				variables={{ cinemaId }}
			>
				{({ cinema: { bonusProgram } }) => {
					const voucherCatalog = bonusProgram.voucherCatalog
						.map(({ bookableUntil, redeemableUntil, ...other }) => ({
							...other,
							bookableUntil: bookableUntil && new Date(bookableUntil),
							redeemableUntil: redeemableUntil && new Date(redeemableUntil),
						}))
						.filter((v) => !v.bookableUntil || isFuture(v.bookableUntil));
					const ownedVouchers = get(user, 'bonusProgramMembership.ownedVouchers', [])
						.map(
							({
								qrCode,
								valid,
								redeemedDatetime,
								createdDatetime,
								voucherClass: { redeemableUntil, ...voucherClass },
							}) => ({
								...voucherClass,
								qrCode,
								redeemableUntil: redeemableUntil && new Date(redeemableUntil),
								valid,
								available: valid && !redeemedDatetime,
								redeemedDatetime: redeemedDatetime && new Date(redeemedDatetime),
								createdDatetime: createdDatetime && new Date(createdDatetime),
							})
						)
						// TODO  Don't we have to filter for valid, redeemable, etc.?
						.filter((v) => !v.redeemedDatetime);
					return (
						<Wrapper>
							<Alert {...{ onAbort: this.handleCloseAlert, ...alert }} />
							<PoseGroup>
								<PoseWrapper key={1}>
									<Title>Voucher / Bonuskarte:</Title>
									<BonusCardWrapper>
										<QRCodeCard
											key={key}
											cinemaId={cinemaId}
											cameraScannerEnabled={cameraScannerEnabled}
											onValueSet={this.handleQRCodeValueSet}
											ref={this.qrRef}
											// qrCode={'427249711'}
										/>
									</BonusCardWrapper>
									<SectionSeparator />
									<Title>Prämie:</Title>
									<VoucherSelectWrapper>
										{voucher ? (
											<SelectButton
												iconName={voucher.iconName}
												title={voucher.title}
												onClick={this.handleToggleVoucherSelection}
											/>
										) : (
											<Typography variant="caption">
												{voucherSelection ? 'PRÄMIE AUSWÄHLEN' : 'ZUERST QR CODE SCANNEN'}
											</Typography>
										)}
									</VoucherSelectWrapper>
								</PoseWrapper>
								{voucherSelection && (
									<PoseWrapper key={2}>
										<VoucherWallWrapper>
											<VoucherHeading>Vorhandene Prämien des Nutzer</VoucherHeading>
											{ownedVouchers.length > 0 ? (
												ownedVouchers.map((voucherDetails) => (
													<VoucherCard
														key={voucherDetails.id}
														{...voucherDetails}
														available={isVoucherAvailable(voucherDetails, user)}
														onClick={this.handleVoucherSelect}
													/>
												))
											) : (
												<VoucherCardPlaceholder>
													<Typography variant="caption">KEINE PRÄMIEN</Typography>
												</VoucherCardPlaceholder>
											)}
										</VoucherWallWrapper>
										<VoucherWallWrapper>
											<VoucherHeading>
												Weitere verfügbare Prämien ({coinBalance} Punkte verfügbar)
											</VoucherHeading>
											{voucherCatalog.map((voucherDetails) => (
												<VoucherCard
													key={voucherDetails.id}
													{...voucherDetails}
													available={isVoucherAvailable(voucherDetails, user)}
													onClick={this.handleVoucherSelect}
												/>
											))}
										</VoucherWallWrapper>
									</PoseWrapper>
								)}
								<PoseWrapper key={3}>
									<SectionSeparator />
									<SubmitWrapper>
										<Button style={iconStyle} onClick={this.handleReset}>
											Zurücksetzten
										</Button>
										<Infobox
											content={
												!completed &&
												[
													user ? null : 'Kein Nutzer identifiziert',
													voucher ? null : 'Keine Prämie ausgewählt',
												].map(
													(text) =>
														text && (
															<ErrorItem key={text}>
																<ErrorIcon style={iconBulletStyle} />
																{text}
															</ErrorItem>
														)
												)
											}
										>
											<Button
												variant="contained"
												color="primary"
												disabled={!completed}
												onClick={this.handleSubmit}
											>
												{completed ? (
													<CheckIcon style={iconStyle} />
												) : (
													<ErrorIcon style={iconStyle} />
												)}{' '}
												Prämie Einlösen
											</Button>
										</Infobox>
									</SubmitWrapper>
								</PoseWrapper>
							</PoseGroup>
						</Wrapper>
					);
				}}
			</WithData>
		);
	};
}



// WEBPACK FOOTER //
// ./src/screens/Redeem.js